import PropTypes from "prop-types";
import React from "react";
import { Button } from "react-bootstrap";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { FormattedMessage, intlShape } from "react-intl";
import { Field, FieldArray, formValueSelector, reduxForm, change as changeFieldValue } from "redux-form";
import {
  RadioGroup,
  SelectInput,
  TextInput,
  DayPicker,
  CurrencyInput,
} from "../../../../common/form";
import employeeTypeChoices from "./employeeTypeChoices";
import payrollFrequencyTypes from "./payrollFrequencyTypes";
import { PREMIUM_SIMULATOR_VALIDATION_RULES } from "./premiumSimulatorValidationRules";
import validationUtils from "../../../../../utils/ValidationUtils";
import { TERMINATED } from "../../enums/clientStatusTypes";
import userHasAccess from "../../../../../utils/userHasAccess";
import { FINTECH } from "../../../../../utils/Roles";
import * as dataActions from "../../../../../utils/data/dataActions";
import { MAX_DECIMAL } from "../../../../common/Configurations/DecimalConfiguration";
import RenderGuarantees from "../members/member/coverage/RenderGuarantees";
import { WAIVED } from "../members/member/coverage/enums/memberCoverageGuaranteeTypes";
import { MessageBox } from "../../../../common";
import {PRICE_LIST, MEMBER, MEMBER_MULTIPLIER} from "../../../admin/guarantees/enums/guaranteeTypes";

const FORM_NAME = "premiumSimulator";

class PremiumSimulatorFields extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.isClassSelected = this.isClassSelected.bind(this);
    this.renderGuaranteesPart = this.renderGuaranteesPart.bind(this);
    this.getClassGuarantees = this.getClassGuarantees.bind(this);
    this.getConfiguredGuarantees = this.getConfiguredGuarantees.bind(this);

    this.hasChangedGuaranteesConfiguration = false;
  }

  componentWillReceiveProps(nextProps) {
    const { classId, memberId } = this.props;
    const nextClassId = nextProps.classId;
    const nextMemberId = nextProps.memberId;

    if (classId && nextClassId != classId) this.hasChangedGuaranteesConfiguration = true;
    else if (nextMemberId != memberId) this.hasChangedGuaranteesConfiguration = false;
  }

  isClassSelected() {
    return !!this.props.classId && this.props.classId !== " ";
  }

  getClassGuarantees() {
    const classClientCoverages = this.props.classClientCoverages;
    if (classClientCoverages.length == 0) return [];

    return classClientCoverages.map((coverage) => coverage);
  }

  allowanceBlur(commissionRate, target, event, newValue) {
    this.props.changeFieldValue(
      FORM_NAME,
      target,
      (newValue - newValue * (commissionRate / 100)).toFixed(MAX_DECIMAL)
    );
  }

  safeAssign(target, source, keys) {
    keys.forEach(k => {
      if (source[k] != null) {
        target[k] = source[k];
      }
    });
    return target;
  }

  getConfiguredGuarantees(classGuarantees, memberGuarantees) {
    const { authLanguageCode, existingEmployee, isClassModified } = this.props;

    return classGuarantees.map((cg) => {
      let match = false;
      const clientCoverage = cg.clientCoverage;
      const classGuarantee = clientCoverage.guarantee;
      const isTypeMember = [MEMBER.value, MEMBER_MULTIPLIER.value].includes(clientCoverage.guarantee.guaranteeType);
      const memberHasCoverage = memberGuarantees.length > 0;
      const isGuaranteeMustBeChecked = isTypeMember ? false : true;

      if (memberHasCoverage) {
        match = memberGuarantees.find((mg) => mg.guarantee.guaranteeId === classGuarantee.guaranteeId);
      }

      let coverageType = null;
      
      if (existingEmployee && !match && isClassModified) coverageType = WAIVED.value;

      let field = {
        guaranteeId: classGuarantee.guaranteeId,
        clientCoverageId: clientCoverage.clientCoverageId,
        name: classGuarantee.localizations[authLanguageCode].name,
        ageOfPolicyDecrease: clientCoverage.ageOfPolicyDecrease,
        ageOfPolicyExpiration: clientCoverage.ageOfPolicyExpiration,
        isTypeMember: isTypeMember,
        guarantee: classGuarantee,
        commissionRate: clientCoverage.commissionRate,
        coverageType: coverageType,
        allowanceBlur: this.allowanceBlur.bind(
          this,
          clientCoverage.commissionRate
        ),
      };

      if (existingEmployee) {
        // If the class has changed from within the same member, we keep the actual guarantee configuration from fields
        // Else we heritate from the db values
        field.checked = this.hasChangedGuaranteesConfiguration ? !!match && match.checked || false : !!match;
      } else {
        field.checked = memberHasCoverage ? match && match.checked || false : isGuaranteeMustBeChecked;
      }

      if (!!match) {
        field = this.safeAssign({ ...field }, match, [
          "ageOfPolicyDecrease",
          "ageOfPolicyExpiration",
          "coverageType",
          "allowance",
          "formulaMspa",
          "invoicedClientRate",
          "volume"
        ]);

        if (match.allowance != null) {
          field.supplierRate = (
            match.allowance - match.allowance * (clientCoverage.commissionRate / 100)
          ).toFixed(MAX_DECIMAL);
        }
      }

      return field;
    });
  }

  renderGuaranteesPart() {
    const availableGuarantees = this.getClassGuarantees();
    const {submitting, isTerminatedClient, authLanguageCode, fetched, classId, memberId, existingEmployee} = this.props;
    let memberGuarantees;
    if (existingEmployee && !this.hasChangedGuaranteesConfiguration){
      memberGuarantees = this.props.initialValues.guarantees || [];
    } else {
      memberGuarantees = this.props.memberCoverageGuarantees || [];
    }
    const configuredGuarantees = this.getConfiguredGuarantees(availableGuarantees, memberGuarantees);
    const displayGuarantees = (this.isClassSelected() && !isObjEmpty(configuredGuarantees));

    return (
      <div className="sub-group">
        <legend>
          <FormattedMessage id="memberCoverages.form.guarantees" />
        </legend>
        <div className="field-array">
            {displayGuarantees ? (
                <FieldArray
                  name="memberCoverageGuarantees"
                  langCode={authLanguageCode}
                  guarantees={configuredGuarantees}
                  fetched={fetched}
                  component={RenderGuarantees}
                  formName={FORM_NAME}
                  classId={classId}
                  readOnly={submitting || isTerminatedClient}
                  memberId={memberId}
                  ratePeriodType={this.props.ratePeriodType}
                  volumeRequired={true}
                  invoicedClientRateRequired={true}
                  allowanceRequired={true}
                  supplierRateRequired={true}
                  mspaRequired={true}
                  ageExpirationRequired={true}
                  ageDecreaseRequired={true}
                />
            ) : (
            <div>
              <FormattedMessage id="clients.client.premiumSimulator.form.noClassSelection" />
            </div>
            )
          }
        </div>
      </div>
    );
  }

  render() {
    const { formatMessage } = this.context.intl;
    const {
      onEmployeeTypeChange,
      onMemberChange,
      classOptions,
      divisionOptions,
      existingEmployee,
      handleSubmit,
      invalid,
      languages,
      memberOptions,
      provinces,
      submitting,
      clientStatusType
    } = this.props;
    const isTerminatedClient = clientStatusType === TERMINATED.value && !userHasAccess(FINTECH);
    return (
      <form onSubmit={handleSubmit}>
        <Field
          id="employeeType"
          name="employeeType"
          onChange={onEmployeeTypeChange}
          component={RadioGroup}
          radios={employeeTypeChoices}
          label={
            <FormattedMessage id="clients.client.premiumSimulator.form.employeeType" />
          }
          disabled={submitting || isTerminatedClient}
        />
        {existingEmployee && (
          <Field
            id="memberId"
            name="memberId"
            onChange={onMemberChange}
            component={SelectInput}
            options={memberOptions}
            aria-label={formatMessage({
              id: "clients.client.premiumSimulator.form.member",
            })}
            disabled={submitting || isTerminatedClient}
          />
        )}
        <Field
          id="payrollFrequency"
          name="payrollFrequency"
          component={SelectInput}
          required={true}
          options={payrollFrequencyTypes}
          sortOptions={false}
          label={
            <FormattedMessage id="clients.client.premiumSimulator.form.payrollFrequency" />
          }
          disabled={submitting || isTerminatedClient}
        />
        <Field
          id="languageCode"
          name="languageCode"
          component={SelectInput}
          required={true}
          options={languages}
          label={
            <FormattedMessage id="clients.client.premiumSimulator.form.language" />
          }
          disabled={submitting || isTerminatedClient}
        />
        <fieldset name="employeeDetails">
          <div className="sub-group">
            <legend>
              <FormattedMessage id="clients.client.premiumSimulator.form.employeeDetails" />
            </legend>
            <Field
              id="lastName"
              name="lastName"
              type="text"
              component={TextInput}
              required={true}
              label={
                <FormattedMessage id="clients.client.premiumSimulator.form.lastName" />
              }
              readOnly={existingEmployee}
              disabled={submitting || isTerminatedClient}
            />
            <Field
              id="firstName"
              name="firstName"
              type="text"
              component={TextInput}
              required={true}
              label={
                <FormattedMessage id="clients.client.premiumSimulator.form.firstName" />
              }
              readOnly={existingEmployee}
              disabled={submitting || isTerminatedClient}
            />
            <Field
              id="provinceCode"
              name="provinceCode"
              component={SelectInput}
              required={true}
              options={provinces}
              label={
                <FormattedMessage id="clients.client.premiumSimulator.form.provinceCode" />
              }
              disabled={submitting || isTerminatedClient}
            />
            <Field
              id="classId"
              name="classId"
              component={SelectInput}
              required={true}
              options={classOptions}
              onChange={this.props.onClassChange}
              label={
                <FormattedMessage id="clients.client.premiumSimulator.form.classId" />
              }
              disabled={submitting || isTerminatedClient}
            />
            <Field
              id="divisionId"
              name="divisionId"
              component={SelectInput}
              required={true}
              options={divisionOptions}
              label={
                <FormattedMessage id="clients.client.premiumSimulator.form.divisionId" />
              }
              disabled={submitting || isTerminatedClient}
            />
            <Field
              id="birthdate"
              name="birthdate"
              component={DayPicker}
              required={true}
              label={
                <FormattedMessage id="clients.client.premiumSimulator.form.birthdate" />
              }
              readOnly={existingEmployee}
              disabled={existingEmployee || submitting || isTerminatedClient}
            />
            <Field
              id="salary"
              name="salary"
              component={CurrencyInput}
              required={true}
              label={
                <FormattedMessage id="clients.client.premiumSimulator.form.salary" />
              }
              disabled={submitting || isTerminatedClient}
            />
          </div>
          {this.renderGuaranteesPart()}
        </fieldset>

        {!this.props.areAllGuaranteesSameCoverageType && (
          <MessageBox className="alert-warning">
            <FormattedMessage id="clients.client.premiumSimulator.form.multipleCoverageTypeSelection" />
          </MessageBox>
        )}

        <Button
          type="submit"
          bsStyle="primary"
          disabled={invalid || submitting || isTerminatedClient}
        >
          <FormattedMessage id="clients.client.premiumSimulator.form.generate" />
        </Button>
      </form>
    );
  }
}

PremiumSimulatorFields.contextTypes = {
  intl: intlShape,
};

PremiumSimulatorFields.propTypes = {
  onEmployeeTypeChange: PropTypes.func,
  onMemberChange: PropTypes.func,
  onClassChange: PropTypes.func,
  classOptions: PropTypes.array,
  divisionOptions: PropTypes.array,
  existingEmployee: PropTypes.bool,
  handleSubmit: PropTypes.func,
  invalid: PropTypes.bool,
  memberOptions: PropTypes.array,
  languages: PropTypes.array,
  provinces: PropTypes.array,
  onSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  clientStatusType: PropTypes.string,
  initialValues: PropTypes.object,
  classId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  dataActions: PropTypes.object,
  classClientCoverages: PropTypes.array,
  changeFieldValue: PropTypes.func,
  authLanguageCode: PropTypes.string,
  isTerminatedClient: PropTypes.bool,
  areAllGuaranteesSameCoverageType: PropTypes.bool,
  fetched: PropTypes.bool,
  ratePeriodType: PropTypes.string,
  memberId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isClassModified: PropTypes.bool
};

function isObjEmpty(obj) {
  return (Object.keys(obj).length === 0);
}

function mapDispatchToProps(dispatch) {
  return {
    changeFieldValue: bindActionCreators(changeFieldValue, dispatch),
    dataActions: bindActionCreators(dataActions, dispatch),
  };
}

function mapStateToProps(state) {
  const selector = formValueSelector(FORM_NAME);
  const classId = selector(state, "classId");
  const memberId = selector(state, "memberId");
  const memberCoverageGuarantees = selector(state, "memberCoverageGuarantees") || [];

  const data = state.data.classClientCoverages && state.data.classClientCoverages || {};

  const classClientCoverages = data.classClientCoverages || [];
  const authLanguageCode = state.auth.user.languageCode;
  const ratePeriodType = state.data.clients.active.ratePeriodType;

  const areAllGuaranteesSameCoverageType = new Set(
    memberCoverageGuarantees.filter(g => g.guarantee.guaranteeType === PRICE_LIST.value && g.checked).map(g => g.coverageType)
  ).size < 2;

  return {
    classClientCoverages: classClientCoverages,
    authLanguageCode: authLanguageCode,
    fetched:  isObjEmpty(classClientCoverages) ? false : true,
    classId: classId,
    memberCoverageGuarantees: memberCoverageGuarantees,
    memberId: memberId,
    ratePeriodType: ratePeriodType,
    areAllGuaranteesSameCoverageType: areAllGuaranteesSameCoverageType
  };
}

export default connect(mapStateToProps, mapDispatchToProps, null, {
  withRef: true
})(
  reduxForm({
    form: FORM_NAME,
    enableReinitialize: true,
    validate: (values) =>
      validationUtils.validate(values, PREMIUM_SIMULATOR_VALIDATION_RULES),
  })(PremiumSimulatorFields)
);


// WEBPACK FOOTER //
// ./src/components/dashboard/clients/client/premiumSimulator/PremiumSimulatorFields.js